import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { CORE_API_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${CORE_API_BASE_URL}/clients`;

export default function useClientIDs(page = 0, pageSize = 10) {
  const authToken = sessionStorage.getItem("authToken");
  const [clientIDs, setClientIDs] = useState([]);
  const [selectedID, setSelectedID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  };
  
  const fetchClientIDs = useCallback(
    async () => {
      if (!authToken) return;


      try {
        const response = await apiClient.get(
          `${API_BASE_URL}?page=${page}&size=${pageSize}`
        );

        const {
          content = [],
          totalPages = 1,
          totalElements = 0,
        } = response.data;

        setClientIDs(content.map(formatClientData));
        setTotalPages(totalPages);
        setTotalElements(totalElements);

      } catch (err) {
        handleAxiosErrors(err, "fetch client IDs");
      }
    },
    [authToken, page, pageSize]
  );
  

  /** -------------------- Fetch Single Client -------------------- **/

  const searchClients = useCallback(async (query, status) => {
  if (!authToken) return;
  setLoading(true);

  try {
    const BASE_URL = `/api/v1/clients`;
    const trimmedQuery = query?.trim() || "";
    
    // Create params object
    const params = new URLSearchParams();
    
    // 1. Always add status if it's not "ALL"
    if (status && status !== "ALL") {
      params.append("status", status);
    }

    // 2. Add query only if 3+ characters
    if (trimmedQuery.length >= 3) {
      params.append("q", trimmedQuery);
    }

    // 3. DECISION: If we have a query OR a specific status, use /search.
    // Otherwise, use the base list URL.
    const isFiltered = params.has("status") || params.has("q");
    const endpoint = isFiltered ? `${BASE_URL}/search` : BASE_URL;
    
    const queryString = params.toString();
    const finalUrl = queryString ? `${endpoint}?${queryString}` : endpoint;

    const response = await apiClient.get(finalUrl);
    const { content = [], totalPages = 1, totalElements = 0 } = response.data;

    setClientIDs(content.map(formatClientData));
    setTotalPages(totalPages);
    setTotalElements(totalElements);
  } catch (err) {
    setError(err.response?.data?.message || err.message);
  } finally {
    setLoading(false);
  }
}, [authToken]);

  // Fetch one client ID by ID
  const fetchClientIDById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`${API_BASE_URL}/${id}`);
        setSelectedID(res.data);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch client ID details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Update an API key
  const updateClientID = useCallback(
    async (id, updatedData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.put(`${API_BASE_URL}/${id}`, updatedData);

        // Update local state
        setClientIDs((prev) =>
          prev.map((key) => (key.id === id ? res.data : key))
        );

        // If currently selected key matches, update it
        setSelectedID((prev) => (prev && prev.id === id ? res.data : prev));

        toast.success("Client ID updated successfully!", { theme: "colored" });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "Update client ID");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchClientIDs();
  }, [authToken, fetchClientIDs]);

  return {
    clientIDs,
    selectedID,
    loading,
    error,
    fetchClientIDs,
    searchClients,
    fetchClientIDById,
    updateClientID,
    setSelectedID,
    totalPages,
    totalElements,
  };
}


const formatClientData = (client = {}) => {
  return {
    // These match your accessorKey strings exactly
    id: client.id || null,
    apiKeyId: client.apiKeyId || null,
    status: client.status || "UNKNOWN",
    createdAt: client.createdAt || null,
    
    // Pass everything else through in case the 'actions' need them
    ...client 
  };
};
